import { type RefObject, useEffect } from 'react';
import { Swiper as SwiperType } from 'swiper/types';

const CHEVRON_NEXT_SELECTOR = '.vds-carousel__chevron--next';

type CarouselVariant = 'default' | 'peek-ahead';

export interface UsePeekAheadNavigationOptions {
  swiperRef: RefObject<SwiperType | null>;
  navigationEnabled: boolean;
  variant: CarouselVariant;
  allowSlideNext: boolean | undefined;
  isSwiperReady: boolean;
}

/**
 * Peek-ahead + `allowSlideNext={false}`: consumers (e.g. PeekAheadCarousel) drive
 * navigation via Swiper's `navigationNext` event while blocking native slideNext.
 * Custom `<button>` chevrons get native `disabled` + `pointer-events: none` at
 * `isEnd`; keep the next chevron interactive and emit `navigationNext` on activation.
 *
 * Capture-phase `stopImmediatePropagation` prevents Swiper's bubble listener from
 * double-emitting `navigationNext`. It also blocks React's delegated `onClick`, so
 * `nextButtonProps.onClick` does not run in this mode (see `nextButtonProps` JSDoc).
 */
export function usePeekAheadNavigation({
  swiperRef,
  navigationEnabled,
  variant,
  allowSlideNext,
  isSwiperReady,
}: UsePeekAheadNavigationOptions): void {
  useEffect(() => {
    if (
      !navigationEnabled ||
      variant !== 'peek-ahead' ||
      allowSlideNext !== false ||
      !isSwiperReady
    )
      return;
    const swiperEl = swiperRef.current?.el as HTMLElement | undefined;
    const carouselContainer = swiperEl?.parentElement;
    const nextEl = carouselContainer?.querySelector(
      CHEVRON_NEXT_SELECTOR,
    ) as HTMLButtonElement | null;
    if (!nextEl) return;

    const apply = (): void => {
      if (nextEl.disabled) nextEl.disabled = false;
      if (nextEl.getAttribute('tabindex') !== '0') nextEl.setAttribute('tabindex', '0');
      if (nextEl.getAttribute('aria-disabled') !== 'false')
        nextEl.setAttribute('aria-disabled', 'false');
    };
    apply();
    const disabledObserver = new MutationObserver(apply);
    disabledObserver.observe(nextEl, {
      attributes: true,
      attributeFilter: ['disabled', 'tabindex', 'aria-disabled'],
    });

    const onNextClick = (event: Event): void => {
      const swiper = swiperRef.current;
      if (!swiper || swiper.params.allowSlideNext !== false) return;
      event.preventDefault();
      // Block Swiper's bubble handler (double navigationNext); React onClick won't run
      event.stopImmediatePropagation();
      swiper.emit('navigationNext');
    };
    nextEl.addEventListener('click', onNextClick, true);

    return () => {
      disabledObserver.disconnect();
      nextEl.removeEventListener('click', onNextClick, true);
    };
  }, [navigationEnabled, variant, allowSlideNext, isSwiperReady, swiperRef]);
}
